import { useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '../ui/Button'
import { ProgressBar } from '../ui/ProgressBar'
import { StepServiceType } from './StepServiceType'
import { StepAquariumDetails } from './StepAquariumDetails'
import { StepLocation } from './StepLocation'
import { StepBudget } from './StepBudget'
import { StepContact } from './StepContact'
import { StepPhotos } from './StepPhotos'
import { StepConfirmation } from './StepConfirmation'
import { useTracking } from '../../hooks/useTracking'
import { useFormPersistence } from '../../hooks/useFormPersistence'
import { submitLead } from '../../lib/api'

export interface FormData {
  // Contact
  name: string
  email: string
  phone?: string
  
  // Service
  service_type: string
  aquarium_size?: string
  aquarium_type?: string
  water_type?: string
  
  // Location & Timeline
  zip_code?: string
  urgency?: string
  budget?: string
  notes?: string
  
  // Photos
  photos?: string[]
}

const TOTAL_STEPS = 6

const stepNames = [
  'service_type',
  'aquarium_details',
  'location',
  'budget',
  'contact',
  'photos',
]

// Validation schemas per step
const getStepValidation = (step: number): (keyof FormData)[] => {
  switch (step) {
    case 1: return ['service_type']
    case 5: return ['name', 'email']
    default: return []
  }
}

export function FormWizard() {
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [leadId, setLeadId] = useState<string | null>(null)

  const { getTracking, onStepComplete, onFormComplete } = useTracking()

  const methods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      service_type: '',
      aquarium_size: '',
      aquarium_type: '',
      water_type: '',
      zip_code: '',
      urgency: '',
      budget: '',
      notes: '',
      photos: [],
    },
  })

  const { clearSavedData } = useFormPersistence(methods, currentStep <= TOTAL_STEPS)

  const validateStep = async (): Promise<boolean> => {
    const fieldsToValidate = getStepValidation(currentStep)
    if (fieldsToValidate.length === 0) return true
    
    const result = await methods.trigger(fieldsToValidate)
    return result
  }

  const nextStep = async () => {
    const isValid = await validateStep()
    if (!isValid) return

    // Track step completion
    onStepComplete(currentStep, stepNames[currentStep - 1], {
      service_type: methods.getValues('service_type'),
    })

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1)
    } else {
      // Submit form
      await handleSubmit()
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const formData = methods.getValues()
      const tracking = getTracking()

      const result = await submitLead({
        ...formData,
        ...tracking,
      })

      if (result.success && result.id) {
        setLeadId(result.id)
        onFormComplete(result.id)
        clearSavedData()
        setCurrentStep(TOTAL_STEPS + 1) // Move to confirmation
      } else {
        setSubmitError(result.error || 'Submission failed. Please try again.')
      }
    } catch (error) {
      console.error('Submit error:', error)
      setSubmitError('An unexpected error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <StepServiceType />
      case 2: return <StepAquariumDetails />
      case 3: return <StepLocation />
      case 4: return <StepBudget />
      case 5: return <StepContact />
      case 6: return <StepPhotos />
      case 7: return <StepConfirmation leadId={leadId || undefined} />
      default: return null
    }
  }

  const isConfirmation = currentStep > TOTAL_STEPS

  return (
    <FormProvider {...methods}>
      <div className="w-full max-w-2xl mx-auto">
        {/* Progress Bar */}
        {!isConfirmation && (
          <div className="mb-8">
            <ProgressBar currentStep={currentStep} totalSteps={TOTAL_STEPS} />
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {/* Error Message */}
          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
            >
              {submitError}
            </motion.div>
          )}

          {/* Navigation Buttons */}
          {!isConfirmation && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1 || isSubmitting}
                className={currentStep === 1 ? 'invisible' : ''}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </Button>

              <Button
                type="button"
                onClick={nextStep}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {currentStep === TOTAL_STEPS ? (
                  <>
                    Submit Request
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                ) : (
                  <>
                    Continue
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Skip hint */}
        {!isConfirmation && currentStep !== 1 && currentStep !== 5 && (
          <p className="text-center text-sm text-gray-500 mt-4">
            Press Continue to skip optional fields
          </p>
        )}
      </div>
    </FormProvider>
  )
}
